import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfileSettingsModal from './ProfileSettingsModal';
import HowItWorksModal from './HowItWorksModal';
import InviteModal from './InviteModal';


export default function Header({ onStartTour, isOnboarding = false, isTourRunning = false }) {
  const { signOut, profile, updateProfile } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    gender: 'male',
    skill_level: '2.5',
    avatar_url: ''
  });

  // Update profileData when profile changes
  useEffect(() => {
    if (profile) {
      console.log('Updating profile data with:', profile);
      setProfileData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone_number: profile.phone_number || '',
        gender: profile.gender || 'male',
        skill_level: profile.skill_level || '2.5',
        avatar_url: profile.avatar_url || ''
      });
      console.log('Profile updated with avatar:', profile.avatar_url);
    }
  }, [profile]);

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Current profile data:', profileData);

      const updates = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone_number: profileData.phone_number,
        gender: profileData.gender,
        skill_level: profileData.skill_level,
        avatar_url: profileData.avatar_url
      };

      console.log('Submitting profile updates:', updates);
      const { error } = await updateProfile(updates);
      
      if (error) {
        console.error('Profile update error:', error);
        throw error;
      }
      
      console.log('Profile updated successfully');
      setShowProfileSettings(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowProfileMenu(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <header className="bg-primary shadow-sm">
        
        <div className="container mx-auto px-4 h-16 flex justify-between items-center">
          {/* Desktop: Left - How it Works */}
          <div className="hidden md:block flex items-center space-x-6">
            <button
              id="how-it-works-link"
              onClick={() => {
                if (onStartTour) {
                  onStartTour();
                } else {
                  setShowHowItWorks(true);
                }
              }}
              className="text-lg text-white/90 hover:text-white font-medium"
            >
              How it Works
            </button>
          </div>

          {/* Mobile: Empty div for spacing */}
          <div className="md:hidden w-10"></div>

          {/* Center - Logo */}
          <div className="flex-1 text-center">
            <div className="flex flex-col items-center">
              <Link to="/" className="text-3xl font-bold text-white hover:text-accent transition inline-block group">
                <span className="group-hover:text-accent">Tennis</span><span className="text-accent group-hover:text-accent">Now</span><span className="text-white group-hover:text-accent">App.com</span>
              </Link>
              <span className="text-white/80 text-sm mt-1 font-light">
                Connecting the Triangle's Tennis Community
              </span>
            </div>
          </div>
          
          {/* Desktop: Invite Button */}
          <div className="hidden md:block">
            <button
              data-tour="invite"
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Invite
            </button>
          </div>

          {/* Profile - Always visible */}
          <div className="relative">
            <button
              data-tour="profile"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition overflow-hidden"
            >
              {profileData.avatar_url ? (
                <div 
                  className="w-full h-full"
                  style={{
                    backgroundColor: '#f8f9fb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={profileData.avatar_url}
                    alt="Profile"
                    className="w-full h-full"
                    key={profileData.avatar_url}
                  />
                </div>
              ) : (
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              )}
            </button>

            {/* Profile Menu Dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                <button
                  onClick={() => {
                    setShowProfileSettings(true);
                    setShowProfileMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-textDark hover:bg-primary/5"
                >
                  Profile Settings
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-textDark hover:bg-primary/5"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Action Buttons - Below header */}
      <div className="md:hidden bg-white py-4 px-4 shadow-sm">
        <div className="flex justify-center space-x-4">
          <button
            id="how-it-works-link-mobile"
            onClick={() => {
              if (onStartTour) {
                onStartTour();
              } else {
                setShowHowItWorks(true);
              }
            }}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            How it Works
          </button>

          <button
            data-tour="invite"
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Invite
          </button>
        </div>
      </div>

      {/* Modals */}
      <ProfileSettingsModal
        show={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
        onSubmit={handleProfileSubmit}
        profileData={profileData}
        onChange={handleProfileChange}
      />

      <HowItWorksModal
        show={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />

      <InviteModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />

    </>
  );
}

Header.propTypes = {
  onStartTour: PropTypes.func,
  isOnboarding: PropTypes.bool,
  isTourRunning: PropTypes.bool
};